import React, { useState, useEffect } from 'react';
import { NavLink, useParams, useLocation } from 'react-router-dom';
import {
  Home,
  Calendar,
  Users,
  CreditCard,
  Settings,
  Shield,
  Clock,
  CalendarDays,
  UserCheck,
  DoorOpen,
  KeyRound,
  Loader2,
  X,
  Sparkles,
  Library,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchEvent, clearSelectedEvent } from '@/store/eventSlice';
import { useIsMobile } from '@/hooks/use-mobile';
import { useUserType } from '@/hooks/useUserType';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

const regularNavItems = [
  { to: '/app/dashboard', icon: Home, label: 'Dashboard' },
  { to: '/app/events', icon: Calendar, label: 'Eventos' },
  { to: '/app/account', icon: CreditCard, label: 'Cuenta' },
  { to: '/app/settings', icon: Settings, label: 'Configuración' }
];

const catalogosSubMenuItems = [
  { to: '/app/providers', icon: Users, label: 'Proveedores' }
];

const adminNavItem = { to: '/app/admin', icon: Shield, label: 'Admin' };

const eventSubMenuItems = [
  { to: '/guests', icon: UserCheck, label: 'Invitados' },
  { to: '/recepcion', icon: DoorOpen, label: 'Recepción' },
  { to: '/calendar', icon: CalendarDays, label: 'Calendario' },
  { to: '/schedule', icon: Clock, label: 'Cronograma' },
  { to: '/providers', icon: Users, label: 'Proveedores' },
  { to: '/accesos', icon: KeyRound, label: 'Accesos', organizerOnly: true }
];

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen = true, onClose }) => {
  const { eventId } = useParams();
  const location = useLocation();
  const [isEventMenuExpanded, setIsEventMenuExpanded] = useState(!!eventId);
  const [isCatalogosExpanded, setIsCatalogosExpanded] = useState(
    location.pathname.startsWith('/app/providers')
  );
  const [isAdmin, setIsAdmin] = useState(false);
  const isMobile = useIsMobile();
  const { isGuest } = useUserType();

  // Redux state and dispatch
  const dispatch = useAppDispatch();
  const { selectedEvent, loading } = useAppSelector(state => state.event);
  const { user } = useAppSelector(state => state.user);
  
  // Check if user is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('users')
          .select('is_admin')
          .eq('user_id', user.id)
          .single();
          
        if (!error && data) {
          setIsAdmin(!!data.is_admin);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };
    
    checkAdminStatus();
  }, [user]);
  
  // Fetch the current event data when eventId changes
  useEffect(() => {
    if (eventId) {
      dispatch(fetchEvent(eventId));
    } else {
      dispatch(clearSelectedEvent());
    }
  }, [eventId, dispatch]);

  const isEventPage = location.pathname.includes('/app/events/') && eventId;
  const truncateEventName = (name: string) => {
    return name.length > 15 ? name.substring(0, 15) + '...' : name;
  };

  React.useEffect(() => {
    setIsEventMenuExpanded(!!eventId);
  }, [eventId]);

  const isCatalogosActive = catalogosSubMenuItems.some((item) =>
    location.pathname.startsWith(item.to)
  );

  useEffect(() => {
    if (isCatalogosActive) setIsCatalogosExpanded(true);
  }, [isCatalogosActive]);

  // Close sidebar on navigation in mobile view
  const handleNavigation = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  // If mobile and not open, don't render
  if (isMobile && !isOpen) {
    return null;
  }

  // Combine navigation items based on admin/guest status. Guests get NO
  // global nav items (no Dashboard / Eventos / Cuenta / Configuración) —
  // they only see the per-event submenu below.
  const navItems = isGuest
    ? []
    : isAdmin
      ? [...regularNavItems, adminNavItem]
      : regularNavItems;

  // Filter out organizer-only items from the per-event submenu (e.g. Accesos)
  // when the current viewer is a guest.
  const visibleEventSubMenuItems = eventSubMenuItems.filter(
    (item) => !item.organizerOnly || !isGuest,
  );

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <div className={`${isMobile ? 'fixed left-0 top-0 h-full z-50' : 'w-64'} bg-[#213c47] border-r border-slate-700 flex flex-col relative transition-all duration-300`}>
        {/* Overlay pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/20 to-indigo-900/20"></div>
        
        <div className="relative z-10 p-6 border-b border-slate-700/50 flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-8 h-8 text-brand-primary" />
              <h1 className="text-xl font-bold text-white ">
                CoPlanner
              </h1>
            </div>
            <p className="text-slate-300 text-sm mt-1">Planificación de eventos</p>
          </div>
          
          {/* Close button for mobile */}
          {isMobile && onClose && (
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/10">
              <X className="h-5 w-5" />
            </Button>
          )}
        </div>
        
        <nav className="flex-1 p-4 relative z-10 overflow-y-auto">
          <ul className="space-y-2">
            {/* Guest mode: no global nav, only the per-event submenu rendered
                directly so the invitee can navigate inside their event. */}
            {isGuest && eventId && (
              <li>
                <div className="flex items-center px-4 py-2 text-white bg-white/5 rounded-sm border-l-4 border-cyan-700 mb-3">
                  <Calendar className="w-4 h-4 mr-3" />
                  {loading ? (
                    <div className="flex items-center">
                      <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                      <span className="text-sm">Cargando...</span>
                    </div>
                  ) : selectedEvent ? (
                    <span className="text-sm font-medium" title={selectedEvent.nombre}>
                      {truncateEventName(selectedEvent.nombre)}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">Evento</span>
                  )}
                </div>
                <ul className="space-y-1">
                  {visibleEventSubMenuItems.map((subItem) => {
                    const fullPath = `/app/events/${eventId}${subItem.to}`;
                    return (
                      <li key={subItem.to}>
                        <NavLink
                          to={fullPath}
                          className={({ isActive }) =>
                            `flex items-center px-3 py-2 rounded-sm text-sm transition-all duration-200 ${
                              isActive
                                ? 'bg-blue-500/20 text-blue-300 border-l-2 border-cyan-500'
                                : 'text-slate-400 hover:bg-white/5 hover:text-slate-300'
                            }`
                          }
                          onClick={handleNavigation}
                        >
                          <subItem.icon className="w-4 h-4 mr-2" />
                          {subItem.label}
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </li>
            )}
            {navItems.map((item) => {
              const isEventsPage = item.to === '/app/events';
              const isEventActive = isEventsPage && (location.pathname === '/app/events' || isEventPage);
              
              return (
                <React.Fragment key={item.to}>
                <li>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center ${isEventsPage ? 'justify-between' : ''} px-4 py-3 rounded-md transition-all duration-200 ${
                        (isActive && !isEventPage) || isEventActive
                          ? 'bg-white/10 text-white border-l-4 border-cyan-700 shadow-lg'
                          : 'text-slate-300 hover:bg-white/5 hover:text-white hover:translate-x-1'
                      }`
                    }
                    onClick={handleNavigation}
                  >
                    <span className="flex items-center">
                      <item.icon className="w-5 h-5 mr-3" />
                      {item.label}
                    </span>
                    {isEventsPage && (
                      eventId ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )
                    )}
                  </NavLink>

                  {/* Event submenu under Eventos */}
                  {isEventsPage && eventId && (
                    <div className="ml-6 mt-2">
                      <div className="flex items-center px-4 py-2 text-white bg-white/5 rounded-sm border-l-4 border-cyan-700">
                        <Calendar className="w-4 h-4 mr-3" />
                        {loading ? (
                          <div className="flex items-center">
                            <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                            <span className="text-sm">Cargando...</span>
                          </div>
                        ) : selectedEvent ? (
                          <span className="text-sm font-medium" title={selectedEvent.nombre}>
                            {truncateEventName(selectedEvent.nombre)}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">Evento no encontrado</span>
                        )}
                      </div>
                      
                      {/* Event navigation submenu */}
                      <ul className="ml-6 mt-2 space-y-1">
                        {visibleEventSubMenuItems.map((subItem) => {
                          const fullPath = `/app/events/${eventId}${subItem.to}`;
                          return (
                            <li key={subItem.to}>
                              <NavLink
                                to={fullPath}
                                className={({ isActive }) =>
                                  `flex items-center px-3 py-2 rounded-sm text-sm transition-all duration-200 ${
                                    isActive
                                      ? 'bg-blue-500/20 text-blue-300 border-l-2 border-cyan-500'
                                      : 'text-slate-400 hover:bg-white/5 hover:text-slate-300'
                                  }`
                                }
                                onClick={handleNavigation}
                              >
                                <subItem.icon className="w-4 h-4 mr-2" />
                                {subItem.label}
                              </NavLink>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </li>

                {isEventsPage && (
                  <li>
                    <button
                      type="button"
                      onClick={() => setIsCatalogosExpanded((v) => !v)}
                      aria-expanded={isCatalogosExpanded}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-md transition-all duration-200 ${
                        isCatalogosActive
                          ? 'bg-white/10 text-white border-l-4 border-cyan-700 shadow-lg'
                          : 'text-slate-300 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      <span className="flex items-center">
                        <Library className="w-5 h-5 mr-3" />
                        Catálogos
                      </span>
                      {isCatalogosExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>

                    {isCatalogosExpanded && (
                      <ul className="ml-6 mt-2 space-y-1">
                        {catalogosSubMenuItems.map((subItem) => (
                          <li key={subItem.to}>
                            <NavLink
                              to={subItem.to}
                              className={({ isActive }) =>
                                `flex items-center px-3 py-2 rounded-sm text-sm transition-all duration-200 ${
                                  isActive
                                    ? 'bg-blue-500/20 text-blue-300 border-l-2 border-cyan-500'
                                    : 'text-slate-400 hover:bg-white/5 hover:text-slate-300'
                                }`
                              }
                              onClick={handleNavigation}
                            >
                              <subItem.icon className="w-4 h-4 mr-2" />
                              {subItem.label}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                )}
                </React.Fragment>
              );
            })}
          </ul>
        </nav>

        {/* Bottom gradient */}
        <div className="h-20 bg-gradient-to-t from-black/20 to-transparent relative z-10"></div>
      </div>
    </>
  );
};
